import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, Switch } from '@mui/material';
import CustomIconButton from './CustomIconButton';
import refresh from 'packages/novous_web/src/assets/icons/Refresh.svg';
import arrowUp from 'packages/novous_web/src/assets/icons/ArrowBlack.svg';
import arrowDown from 'packages/novous_web/src/assets/icons/ArrowDownBlack.svg';
import dragAndDrop from 'packages/novous_web/src/assets/icons/Drag and Move.svg';
import CustomDropDownInput from './customDropDownInput';

type ColumnObjType = {
  [key: string]: {
    name: string;
    status: boolean;
  };
};

type TilesObjType = {
  [key: string]: {
    name: string;
    status: boolean;
    selected?: string;
  };
};

interface CustomiseTableModalProps {
  open: boolean;
  onClose: () => void;
  tilesObj?: TilesObjType;
  onReset?: () => void;
  title?: string;
  description?: string;
  columnObj: ColumnObjType;
  setTilesObj?: React.Dispatch<React.SetStateAction<TilesObjType>>;
  setColumnObj: React.Dispatch<React.SetStateAction<ColumnObjType>>;
  children?: React.ReactNode;
}
interface OptionType {
  value: string;
  label: string;
  disabled?: boolean;
}

const CustomiseTableModal: React.FC<CustomiseTableModalProps> = ({
  open,
  onClose,
  onReset,
  title = 'Customise Table',
  description = 'Choose which sections to display and arrange them the way you like.',
  columnObj,
  setColumnObj,
  setTilesObj,
  tilesObj,
}) => {
  const [tempColumnObj, setTempColumnObj] = useState<ColumnObjType>(columnObj);
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [dataTableExpanded, setDataTableExpanded] = useState(false);

  // State for managing dropdown selections via React hooks.
  const [tileSelections, setTileSelections] = useState<Record<string, string>>({});

  // Initialize tileSelections from tilesObj when available.
  // FIX: Initialize tileSelections when modal opens or tilesObj changes
  // FIX: Initialize tileSelections from current selections when modal opens
  useEffect(() => {
    if (tilesObj && open) {
      const currentSelections = Object.keys(tilesObj).reduce((acc, key) => {
        // If the tile is disabled (status: false), it means "none" was selected
        // Otherwise, use the tile's name
        acc[key] = tilesObj[key].status ? tilesObj[key].name : 'none';
        return acc;
      }, {} as Record<string, string>);
      setTileSelections(currentSelections);
    }
  }, [tilesObj, open]);

  useEffect(() => {
    if (open) {
      setTempColumnObj(columnObj);
    }
  }, [open, columnObj]);

  const toggleOverview = () => {
    setOverviewExpanded((prev) => !prev);
  };

  const toggleDataTable = () => {
    setDataTableExpanded((prev) => !prev);
  };

  const handleCheckboxChange = (key: keyof ColumnObjType) => {
    setTempColumnObj((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        status: !prev[key].status,
      },
    }));
  };

  // Reset will set all statuses to true.
  const handleReset = () => {
    const resetObj = Object.fromEntries(
      Object.entries(columnObj).map(([key, value]) => [key, { ...value, status: true }]),
    ) as ColumnObjType;
    setTempColumnObj(resetObj);

    // Reset tile selections to show all tiles (no "none" selected)
    if (tilesObj) {
      const resetTileSelections = Object.keys(tilesObj).reduce((acc, key) => {
        acc[key] = tilesObj[key].name; // Reset to original tile names
        return acc;
      }, {} as Record<string, string>);
      setTileSelections(resetTileSelections);
    }

    if (onReset) {
      onReset();
    }
  };

  const handleSave = () => {
    setColumnObj(tempColumnObj);

    if (setTilesObj && tilesObj) {
      // Create updated tiles object with correct status and name based on selections
      const updatedTileObj = Object.keys(tilesObj).reduce((acc, key) => {
        const selectedValue = tileSelections[key];
        const isNoneSelected = selectedValue === 'none';

        acc[key] = {
          ...tilesObj[key],
          name: isNoneSelected ? tilesObj[key].name : selectedValue, // Keep original name if "none" selected
          status: !isNoneSelected, // Set status to false if "none" is selected
        };
        return acc;
      }, {} as TilesObjType);

      console.log(updatedTileObj, '-------------');
      setTilesObj(updatedTileObj);
    }

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Build the complete list of dropdown options from tilesObj.
  const statsOption = useMemo(() => {
    if (tilesObj) {
      const tiles = Object.keys(tilesObj).map((item: string) => ({
        tileCode: tilesObj[item].name,
        tileName: tilesObj[item].name,
        label: tilesObj[item].name,
      }));
      // Add the "None" option.
      tiles.push({
        tileCode: 'none',
        tileName: 'None',
        label: 'None',
      });
      return tiles;
    }
    return [];
  }, [tilesObj]);

  const handleTileChange = (tileKey: string, selectedValue: string) => {
    setTileSelections((prev) => {
      const next = { ...prev, [tileKey]: selectedValue };

      // Immediately reflect changes in the tiles object so other components
      // (like DriverCoachingDataTiles) update live while the modal is open.
      if (setTilesObj && tilesObj) {
        const updatedTileObj = Object.keys(tilesObj).reduce((acc, key) => {
          const sel = next[key];
          const isNoneSelected = !sel || sel === 'none';
          acc[key] = {
            ...tilesObj[key],
            name: isNoneSelected ? tilesObj[key].name : sel,
            status: !isNoneSelected,
            selected: sel || '',
          };
          return acc;
        }, {} as typeof tilesObj);

        try {
          setTilesObj(updatedTileObj as any);
        } catch (err) {
          // ignore if parent can't accept shape
          // console.error('setTilesObj failed', err);
        }
      }

      console.log(`Tile ${tileKey} changed to:`, selectedValue);
      return next;
    });
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      aria-labelledby="customise-table-title"
      aria-describedby="customise-table-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '70%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          overflowY: 'auto',
          maxHeight: '80vh',
        }}
      >
        <div className="flex gap-8 flex-col">
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center">
              <Typography variant="h6">{title}</Typography>
              {onReset && (
                <CustomIconButton
                  text="Reset"
                  icon={<img src={refresh} alt="reset" />}
                  background="transparent"
                  variant="outlined"
                  onClick={handleReset}
                />
              )}
            </div>
            {description && (
              <Typography variant="button" fontWeight={400} color="secondary">
                {description}
              </Typography>
            )}
          </div>
          {tilesObj && (
            <div className="flex flex-col gap-2">
              <div
                className="flex justify-between w-full items-center cursor-pointer"
                onClick={toggleOverview}
              >
                <Typography variant="body1">Top Overview Stats</Typography>
                <img
                  src={overviewExpanded ? arrowUp : arrowDown}
                  width={24}
                  height={24}
                  alt={overviewExpanded ? 'collapse' : 'expand'}
                />
              </div>
              {overviewExpanded && (
                <div className="flex gap-2">
                  {tilesObj &&
                    Object.keys(tilesObj).map((tileKey: string) => {
                      // Determine values selected in other dropdowns.
                      const alreadySelected = Object.keys(tileSelections)
                        .filter((key) => key !== tileKey)
                        .map((key) => tileSelections[key])
                        .filter((val) => val !== '' && val !== 'none');

                      // Filter statsOption and map to OptionType.
                      const filteredOptions: OptionType[] = statsOption
                        .filter((option) => !alreadySelected.includes(option.tileCode))
                        .map((option) => ({ value: option.tileCode, label: option.label }));

                      return (
                        <CustomDropDownInput
                          key={tileKey}
                          label={tileKey}
                          isMandatory={true}
                          options={filteredOptions}
                          value={tileSelections[tileKey] || ''}
                          onChange={(selectedValue: string) => handleTileChange(tileKey, selectedValue)}
                          placeholder="Select Route Code"
                        />
                      );
                    })}
                </div>
              )}
            </div>
          )}

          {/* Section: Data Table Columns */}
          <div className="flex flex-col gap-2">
            <div
              className="flex justify-between w-full items-center cursor-pointer"
              onClick={toggleDataTable}
            >
              <Typography variant="body1">Data Table Columns</Typography>
              <img
                src={dataTableExpanded ? arrowUp : arrowDown}
                width={24}
                height={24}
                alt={dataTableExpanded ? 'collapse' : 'expand'}
              />
            </div>
            {dataTableExpanded && (
              <div
                className="px-4 flex flex-col gap-2 custom-scrollbar"
                style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                  padding: '8px',
                }}
              >
                {Object.entries(tempColumnObj).map(([key, value]) => (
                  <div
                    key={key}
                    className="flex justify-between w-full items-center"
                    style={{
                      height: '56px',
                      gap: '16px',
                      borderRadius: '8px',
                      padding: '16px',
                      background: '#F6F6FD',
                    }}
                  >
                    <div className="flex gap-4 items-center">
                      <img src={dragAndDrop} width={24} height={24} alt="dragAndDrop" />
                      <Typography variant="body2" color="textPrimary">
                        {value.name}
                      </Typography>
                    </div>
                    <Switch
                      checked={value.status}
                      onChange={() => handleCheckboxChange(key as keyof ColumnObjType)}
                      color="primary"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex gap-2 mt-4 items-center justify-between">
          <CustomIconButton
            text="Cancel"
            background="transparent"
            outline={true}
            padding="0px"
            variant="outlined"
            onClick={handleCancel}
          />
          <CustomIconButton
            text="Save"
            outline={true}
            padding="0px"
            variant="contained"
            onClick={handleSave}
          />
        </div>
      </Box>
    </Modal>
  );
};

export default CustomiseTableModal;
